import { http } from '@/lib/http';

export type CustomerStatus = 'active' | 'inactive';

/** Mirrors `repo.BusinessCustomer` in the vizitpro-api swagger spec. */
export interface CustomerDto {
  id: string;
  business_id: string;
  customer_user_id: string | null;
  full_name: string;
  email: string | null;
  phone: string | null;
  service_address: string | null;
  notes: string | null;
  status: CustomerStatus;
  created_at: string;
  updated_at: string;
}

/**
 * Request body for `POST /businesses/{business_id}/customers` — mirrors
 * `controller.createBusinessCustomerRequest`. `full_name` is required; the
 * rest are optional.
 */
export interface CreateCustomerRequest {
  full_name: string;
  email?: string;
  phone?: string;
  notes?: string;
  service_address?: string;
}

/**
 * Request body for `PATCH /businesses/{business_id}/customers/{id}` —
 * mirrors `controller.updateBusinessCustomerRequest`. `full_name`/`email`/
 * `phone` are denormalized from the linked user record and not settable via
 * this endpoint.
 */
export interface UpdateCustomerRequest {
  notes?: string;
  service_address?: string;
  status?: CustomerStatus;
}

export function customersQueryKey(businessId: string, status: CustomerStatus | 'all') {
  return ['customers', businessId, status] as const;
}

export function customerQueryKey(businessId: string, customerId: string) {
  return ['customers', businessId, customerId] as const;
}

/** Calls `GET /businesses/{business_id}/customers`, optionally filtered by status. */
export async function listCustomers(
  businessId: string,
  status?: CustomerStatus,
): Promise<CustomerDto[]> {
  const { data } = await http.get<CustomerDto[]>(
    `/businesses/${businessId}/customers`,
    { params: status ? { status } : undefined },
  );
  return data;
}

/** Calls `GET /businesses/{business_id}/customers/{id}`. */
export async function getCustomer(
  businessId: string,
  customerId: string,
): Promise<CustomerDto> {
  const { data } = await http.get<CustomerDto>(
    `/businesses/${businessId}/customers/${customerId}`,
  );
  return data;
}

/** Calls `POST /businesses/{business_id}/customers`. Caller must be an owner. */
export async function createCustomer(
  businessId: string,
  body: CreateCustomerRequest,
): Promise<CustomerDto> {
  const { data } = await http.post<CustomerDto>(
    `/businesses/${businessId}/customers`,
    body,
  );
  return data;
}

/** Calls `PATCH /businesses/{business_id}/customers/{id}`. Caller must be an owner. */
export async function updateCustomer(
  businessId: string,
  customerId: string,
  body: UpdateCustomerRequest,
): Promise<CustomerDto> {
  const { data } = await http.patch<CustomerDto>(
    `/businesses/${businessId}/customers/${customerId}`,
    body,
  );
  return data;
}
